import { createRouter, createWebHistory } from 'vue-router';
import store from './store';
import Axios from 'axios';

const BASE_URL = import.meta.env.VITE_VERIFY_URL;
const APP_NAME = import.meta.env.VITE_APP_NAME;

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import(/* webpackChunkName: 'home' */ './views/Home.vue'),
      meta: {
        requiresAuth: true,
        title: `Vialto Partners - ${APP_NAME} - Solution for Social Security Compliance`,
      },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import(/* webpackChunkName: 'login' */ './views/user/Login.vue'),
      props: (route) => ({
        success: route.query.success == 'true',
      }),
      meta: {
        guest: true,
        title: `Vialto Partners - ${APP_NAME} Login`,
      },
    },
    {
      path: '/logout',
      name: 'logout',
      component: () => import(/* webpackChunkName: 'logout' */ './views/user/Logout.vue'),
      meta: {
        requiresAuth: true,
        title: `Vialto Partners - ${APP_NAME} Logout`,
      },
    },
    {
      path: '/user/register',
      name: 'new-user',
      component: () => import(/* webpackChunkName: 'new-user' */ './views/user/New.vue'),
      meta: {
        guest: true,
        title: `Vialto Partners - ${APP_NAME} New User`,
      },
    },
    {
      path: '/user/edit',
      name: 'edit-user',
      component: () => import(/* webpackChunkName: 'edit-user' */ './views/user/Edit.vue'),
      meta: {
        requiresAuth: true,
        title: `Vialto Partners - ${APP_NAME} Edit User Profile`,
      },
    },
    {
      path: '/user/additional-information',
      name: 'additional-information',
      component: () =>
        import(
          /* webpackChunkName: 'additional-information' */ './views/user/AdditionalInformation.vue'
        ),
      meta: {
        guest: true,
        title: `Vialto Partners - ${APP_NAME} Additional Information`,
      },
    },
    {
      path: '/user/applications',
      name: 'user-applications',
      component: () =>
        import(/* webpackChunkName: 'additional-information' */ './views/user/MyApplications.vue'),
      meta: {
        requiresAuth: true,
        title: `Vialto Partners - ${APP_NAME} User Applications`,
      },
    },
    {
      path: '/user/registered',
      name: 'user-registered',
      component: () =>
        import(/* webpackChunkName: 'user-registered' */ './views/user/Registered.vue'),
      meta: {
        guest: true,
        title: `Vialto Partners - ${APP_NAME} User Registered`,
      },
    },
    {
      path: '/application/new',
      name: 'new-application',
      component: () => import(/* webpackChunkName: 'application' */ './views/application/New.vue'),
      meta: {
        requiresAuth: true,
        title: `Vialto Partners - ${APP_NAME} New A1 application`,
      },
    },
    {
      path: '/application/thanks',
      name: 'application-thanks',
      component: () =>
        import(/* webpackChunkName: 'application' */ './views/application/Thanks.vue'),
      meta: {
        requiresAuth: true,
        title: `Vialto Partners - ${APP_NAME} Thanks`,
      },
    },
    {
      path: '/admin',
      name: 'admin-home',
      component: () => import(/* webpackChunkName: 'home' */ './views/Home.vue'),
      meta: {
        requiresAuth: true,
        is_admin: true,
        title: `Vialto Partners - ${APP_NAME} Admin`,
      },
    },
    {
      path: '/admin/users/upload',
      name: 'upload-multiple-emails',
      component: () =>
        import(
          /* webpackChunkName: 'upload-multiple-emails' */ './views/admin/EmailsEditMultiple.vue'
        ),
      meta: {
        requiresAuth: true,
        is_admin: true,
        title: `Vialto Partners - ${APP_NAME} Upload Multiple Emails`,
      },
    },
    {
      path: '/admin/users',
      name: 'manage-users',
      component: () =>
        import(/* webpackChunkName: 'manage-users' */ './views/admin/EmailsEdit.vue'),
      meta: {
        requiresAuth: true,
        is_admin: true,
        title: `Vialto Partners - ${APP_NAME} Manage Users`,
      },
    },
    {
      path: '/admin/applications',
      name: 'company-applications',
      component: () =>
        import(
          /* webpackChunkName: 'company-applications' */ './views/admin/CompanyApplications.vue'
        ),
      meta: {
        requiresAuth: true,
        is_admin: true,
        title: `Vialto Partners - ${APP_NAME} All Applications`,
      },
    },
    {
      path: '/superadmin',
      name: 'super-admin-home',
      component: () => import(/* webpackChunkName: 'home' */ './views/Home.vue'),
      meta: {
        requiresAuth: true,
        is_superadmin: true,
        title: `Vialto Partners - ${APP_NAME} Super Admin`,
      },
    },
    {
      path: '/superadmin/companies/new',
      name: 'new-company',
      component: () => import(/* webpackChunkName: 'home' */ './views/super-admin/New.vue'),
      meta: {
        requiresAuth: true,
        is_superadmin: true,
        title: `Vialto Partners - ${APP_NAME} Company Overview`,
      },
    },
    {
      path: '/superadmin/companies',
      name: 'company-overview',
      component: () =>
        import(/* webpackChunkName: 'home' */ './views/super-admin/CompanyOverview.vue'),
      meta: {
        requiresAuth: true,
        is_superadmin: true,
        title: `Vialto Partners - ${APP_NAME} Company Overview`,
      },
    },
    {
      path: '/faq',
      name: 'faq',
      component: () => import(/* webpackChunkName: 'faq' */ './views/info/FAQ.vue'),
      meta: {
        requiresAuth: true,
        title: `Vialto Partners - ${APP_NAME} FAQ`,
      },
    },
    {
      path: '/contact',
      name: 'contact',
      component: () => import(/* webpackChunkName: 'contact' */ './views/info/Contact.vue'),
      meta: {
        requiresAuth: true,
        title: `Vialto Partners - ${APP_NAME} Contact`,
      },
    },
    {
      path: '/conditions',
      name: 'conditions',
      component: () =>
        import(/* webpackChunkName: 'LicensConditions' */ './views/info/LicenseConditions.vue'),
      meta: {
        requiresAuth: false,
        title: `Vialto Partners - ${APP_NAME} License Conditions`,
      },
    },
    {
      path: '/disclaimer',
      name: 'disclaimer',
      component: () => import(/* webpackChunkName: 'Disclaimer' */ './views/info/Disclaimer.vue'),
      meta: {
        requiresAuth: false,
        title: `Vialto Partners - ${APP_NAME} Disclaimer`,
      },
    },
    {
      path: '/cookies',
      name: 'cookies',
      component: () => import(/* webpackChunkName: 'Cookies' */ './views/info/Cookies.vue'),
      meta: {
        requiresAuth: false,
        title: `Vialto Partners - ${APP_NAME} Cookies`,
      },
    },
    {
      path: '/:pathMatch(.*)*',
      name: '404',
      component: () => import(/* webpackChunkName: '404' */ './views/info/404.vue'),
      meta: {
        requiresAuth: false,
        title: `Vialto Partners - ${APP_NAME} 404`,
      },
    },
  ],
});

router.beforeEach((to, from, next) => {
  if (window.location.href.indexOf('localhost') !== -1) {
    // eslint-disable-next-line
    console.warn('Local development mode. Verify check is disabled.');
    store.dispatch('SET_LOADING', false);
    next();
  } else {
    if (
      to.matched.some((record) => record.meta.requiresAuth) &&
      to.matched.some((record) => record.meta.is_superadmin)
    ) {
      if (store.state.role == 'super_admin') {
        Axios.post(
          BASE_URL,
          {
            email: store.state.email.toLowerCase(),
            token: store.state.token,
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        ).then((response) => {
          if (response.data.success) {
            store.dispatch('SET_LOADING', false);
            next();
          } else {
            store.dispatch('SET_LOADING', false);
            next('/login');
          }
        });
      } else {
        next(from);
      }
    } else if (
      to.matched.some((record) => record.meta.requiresAuth) &&
      to.matched.some((record) => record.meta.is_admin)
    ) {
      if (!store.state.email || !store.state.token) {
        store.dispatch('SET_LOADING', false);
        next('/login');
      } else {
        if (store.state.role == 'user_admin' || store.state.role == 'super_admin') {
          Axios.post(
            BASE_URL,
            {
              email: store.state.email.toLowerCase(),
              token: store.state.token,
            },
            {
              headers: {
                'Content-Type': 'application/json',
              },
            },
          ).then((response) => {
            if (response.data.success) {
              store.dispatch('SET_LOADING', false);
              next();
            } else {
              store.dispatch('SET_LOADING', false);
              next('/login');
            }
          });
        } else {
          next(from);
        }
      }
    } else if (to.matched.some((record) => record.meta.requiresAuth)) {
      if (!store.state.email || !store.state.token) {
        next('/login');
      } else {
        Axios.post(
          BASE_URL,
          {
            email: store.state.email.toLowerCase(),
            token: store.state.token,
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        ).then((response) => {
          if (response.data.success) {
            store.dispatch('SET_LOADING', false);
            next();
          } else {
            store.dispatch('SET_LOADING', false);
            next('/login');
          }
        });
      }
    } else {
      store.dispatch('SET_LOADING', false);
      next();
    }
  }
});

export default router;
